import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Flex,
  FormInput,
  Heading,
  IconButton,
  Text,
  toast
} from '@sparrowengg/twigs-react';
import { CloseIcon } from '@sparrowengg/twigs-react-icons';
import React, { useState } from 'react';
import { IparamsType } from '../types';
import { APP_STORE, iparamType } from 'commons/constants';
import axios from 'lib/axios';
import {
  getHeader,
  setLocalStorageValue,
  getLocalStorageValue
} from 'commons/helper';
import { IRootState } from 'commons/types';
import { useSelector } from 'react-redux';

const IparamsModal = ({
  resetModal,
  currentDomain,
  iparams
}: {
  resetModal: () => void;
  iparams: Array<IparamsType>;
  currentDomain: string;
}) => {
  const [iparamsInput, setIparamsInput] = useState({});
  const [loader, setLoader] = useState(false);
  const currentApp = useSelector((state: IRootState) => state.currentApp);
  const installIparams = async () => {
    setLoader(true);
    try {
      const appStore = getLocalStorageValue(APP_STORE);
      const response = (
        await axios.post(
          `ssdk/custom_configs/store?product=${currentDomain}&appVersionId=${currentApp.appVersionId}`,
          {
            iparams: { ...iparamsInput },
            ...(appStore && { store: appStore })
          },
          getHeader()
        )
      ).data;
      if (response?.store) {
        setLocalStorageValue(APP_STORE, response.store);
      }
      toast({
        variant: 'success',
        description: 'Successfully installed parameters'
      });
      resetModal();
      setIparamsInput({});
    } catch (error) {
      console.error(error);
      toast({
        variant: 'error',
        description: 'Error when installing the parameters'
      });
    } finally {
      setLoader(false);
    }
  };

  return (
    <Dialog open>
      <DialogTitle />
      <DialogContent
        css={{
          padding: 0,
          borderRadius: '$xl',
          width: 864,
          background: '#202020',
          color: '$white900'
        }}
      >
        <Flex
          css={{
            padding: '$10 $12',
            borderBottom: '$borderWidths$xs solid $white200'
          }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Flex flexDirection="column" gap="$2">
            <Heading size="h6">Install IParams (Simulation)</Heading>
            <Text size="sm">
              A simulation environment to installing IParams and utilizing the
              specified data within the application
            </Text>
          </Flex>
          <IconButton
            size="lg"
            variant="ghost"
            color="light"
            icon={<CloseIcon />}
            onClick={() => {
              resetModal();
              setIparamsInput({});
            }}
            disabled={loader}
          />
        </Flex>
        <Flex
          flexDirection="column"
          gap="$14"
          css={{
            padding: '$12',
            height: 376,
            overflowY: 'auto',
            label: {
              color: '$white900',
              fontWeight: '$7',
              fontSize: '$sm'
            },
            'input, textarea': {
              background: '$white200 !important',
              color: '$white900',
              border: '$black300 !important',
              boxShadow: 'none !important',
              '&:focus, &:hover, &:active': {
                background: '$white200'
              }
            }
          }}
        >
          {iparams.map((iparam) => (
            <IparamComponent
              iparam={iparam}
              handleIparamInput={(property: string, value: string) =>
                setIparamsInput((prev) => ({ ...prev, [property]: value }))
              }
            />
          ))}
        </Flex>
        <Flex
          alignItems="center"
          gap="$6"
          justifyContent="flex-end"
          css={{
            padding: '$12',
            borderTop: '$borderWidths$xs solid $white200'
          }}
        >
          <Button
            color="error"
            size="xl"
            variant="ghost"
            onClick={() => {
              resetModal()
              setIparamsInput({});
            }}
            disabled={loader}
          >
            No, Go Back
          </Button>
          <Button
            onClick={() => installIparams()}
            size="xl"
            loading={loader}
            disabled={
              !Object.keys(iparamsInput)?.length || loader
            }
          >
            Install Iparams
          </Button>
        </Flex>
      </DialogContent>
    </Dialog>
  );
};

export default IparamsModal;

const IparamComponent = ({
  iparam,
  handleIparamInput
}: {
  iparam: IparamsType;
  handleIparamInput: (property: string, value: string) => void;
}) => {
  const isRequired = iparam?.required.value as unknown as boolean;
  switch (iparam.type.value) {
    case iparamType.API_KEY:
      return (
        <Flex flexDirection="column" gap={'$1'}>
          <FormInput
            size="lg"
            label={iparam.displayName}
            type={iparam?.secure ? 'password' : 'text'}
            required={isRequired}
            onChange={(event) =>
              handleIparamInput(iparam.keyName, event.currentTarget.value)
            }
            requiredIndicator={isRequired}
          />
          {iparam.description && (
            <Text size={'xs'} css={{ color: '$white600' }}>
              {iparam.description}
            </Text>
          )}
        </Flex>
      );
    case iparamType.TEXT:
      return (
        <Flex flexDirection="column" gap={'$1'}>
          <FormInput
            size="lg"
            label={iparam.displayName}
            required={isRequired}
            onChange={(event) =>
              handleIparamInput(iparam.keyName, event.currentTarget.value)
            }
            requiredIndicator={isRequired}
          />
          {iparam.description && (
            <Text size={'xs'} css={{ color: '$white600' }}>
              {iparam.description}
            </Text>
          )}
        </Flex>
      );
    case iparamType.EMAIL:
      return (
        <Flex flexDirection="column" gap={'$1'}>
          <FormInput
            size="lg"
            label={iparam.displayName}
            required={isRequired}
            onChange={(event) =>
              handleIparamInput(iparam.keyName, event.currentTarget.value)
            }
            requiredIndicator={isRequired}
            type="email"
          />
          {!!iparam.description && (
            <Text size={'xs'} css={{ color: '$white600' }}>
              {iparam.description}
            </Text>
          )}
        </Flex>
      );
    case iparamType.PHONE_NUMBER:
      return (
        <Flex flexDirection="column" gap={'$1'}>
          <FormInput
            size="lg"
            label={iparam.displayName}
            required={isRequired}
            onChange={(event) =>
              handleIparamInput(iparam.keyName, event.currentTarget.value)
            }
            requiredIndicator={isRequired}
          />
          {!!iparam.description && (
            <Text size={'xs'} css={{ color: '$white600' }}>
              {iparam.description}
            </Text>
          )}
        </Flex>
      );
    case iparamType.URL:
      return (
        <Flex flexDirection="column" gap={'$1'}>
          <FormInput
            size="lg"
            label={iparam.displayName}
            required={isRequired}
            onChange={(event) =>
              handleIparamInput(iparam.keyName, event.currentTarget.value)
            }
            requiredIndicator={isRequired}
            type="url"
          />
          {!!iparam.description && (
            <Text size={'xs'} css={{ color: '$white600' }}>
              {iparam.description}
            </Text>
          )}
        </Flex>
      );
    case iparamType.NUMBER:
      return (
        <Flex flexDirection="column" gap={'$1'}>
          <FormInput
            size="lg"
            label={iparam.displayName}
            required={isRequired}
            onChange={(event) =>
              handleIparamInput(iparam.keyName, event.currentTarget.value)
            }
            requiredIndicator={isRequired}
            type="number"
          />
          {!!iparam.description && (
            <Text size={'xs'} css={{ color: '$white600' }}>
              {iparam.description}
            </Text>
          )}
        </Flex>
      );
    case iparamType.DATE:
      return (
        <Flex flexDirection="column" gap={'$1'}>
          <FormInput
            size="lg"
            label={iparam.displayName}
            required={isRequired}
            onChange={(event) =>
              handleIparamInput(iparam.keyName, event.currentTarget.value)
            }
            requiredIndicator={isRequired}
            type="date"
          />
          {!!iparam.description && (
            <Text size={'xs'} css={{ color: '$white600' }}>
              {iparam.description}
            </Text>
          )}
        </Flex>
      );
    default:
      return null;
  }
};
